import { useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import type { BookShelf, User } from 'api/data.types'
import { getHighlightsBooks } from 'api/queries/reader'
import { getBooksBadges } from 'utils/badges'
import PremiumLock from 'utils/premiumLock'
import type { CoverBookShelfProps } from 'components/modular/shelves/book-shelf/BookShelf.types'
import { post_event } from 'utils/analytics'
import { INTERMEDIATE_DETAILS_BOOK_PATH, SHOW_SHELF } from 'routes/paths'
import { shouldUseNextjsBookIntermediate } from 'utils/identify-modules/IdentifyModules'
import type { Module } from 'api/data.types'
import { allBookshelvesQuery } from 'api/queries'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useHistory } from 'react-router-dom'

const useHighlightsBookShelf = (
  contentFilter: Module,
  isStudentView,
  degree
) => {
  const client = useApolloClient()
  const history = useHistory()
  const [content, setContent] = useState([])
  const [highlightId, setHighlightId] = useState<number>()
  const [isLoading, setIsLoading] = useState(true)
  const { user } = useUserInfo()

  useEffect(() => {
    setIsLoading(true)
    isStudentView ? getHighlightsAsStudent() : getHighlights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [degree])

  const concatHighlightsBooks = data => {
    const booksHightlights = data.map(highlight => {
      const booksHighlight = highlight.bookShelfItems.map(book => {
        return { ...book }
      })
      return [...booksHighlight]
    })
    const highlights = {
      id: data[0].id as number,
      name: data[0].name as string,
      bookShelfItems: booksHightlights[0].concat(booksHightlights[1])
    }
    return highlights
  }

  const getHighlights = () => {
    client
      .query<{
        me: User
      }>({
        query: getHighlightsBooks,
        variables: {
          contentFilter
        }
      })
      .then(res => {
        const highlightsLength = res?.data?.me?.reader?.highlightsv2?.length
        const highlightsData = res?.data?.me?.reader?.highlightsv2
        if (highlightsLength) {
          highlightsLength > 1
            ? setBooks(concatHighlightsBooks(highlightsData))
            : setBooks(highlightsData[0])
        }
        setIsLoading(res.loading)
      })
      .catch(err => console.log(`Oops, aconteceu um erro: ${err.message}`))
  }

  const getHighlightsAsStudent = () => {
    client
      .query<{
        listExistingBookshelf: BookShelf[]
      }>({
        query: allBookshelvesQuery,
        variables: {
          contentFilter,
          degreeRange: degree,
          isHighlight: true,
          acervoType: null,
          active: true
        }
      })
      .then(res => {
        const highlightsLength = res?.data?.listExistingBookshelf?.length
        const highlightsData = res?.data?.listExistingBookshelf
        if (highlightsLength) {
          highlightsLength > 1
            ? setBooks(concatHighlightsBooks(highlightsData))
            : setBooks(highlightsData[0])
        }
        setIsLoading(res.loading)
      })
      .catch(err => console.log(`Oops, aconteceu um erro: ${err.message}`))
  }

  const setBooks = data => {
    const { bookShelfItems, id } = data

    const books = bookShelfItems.map(highlightBook => {
      const degree = getBooksBadges({
        book: highlightBook.book,
        user
      })

      return {
        ...highlightBook.book,
        title: highlightBook.book.name,
        language: highlightBook.book.language,
        degreesRange: [{ tagName: degree?.name, fullName: degree?.fullName }]
      }
    })

    setContent(books)
    setHighlightId(id)
  }

  const onClickBook = (book: CoverBookShelfProps) => {
    if (book.premiumLock) {
      PremiumLock.sendBookClickEvent(book)
    }
    post_event(`Destaques/ClickCapaLivro/${contentFilter}`, book.slug)
    if (shouldUseNextjsBookIntermediate()) {
      window.location.href = `/app-v2/book/intermediate/${book.slug}`
      return
    }
    history.push(INTERMEDIATE_DETAILS_BOOK_PATH(book.slug))
  }

  const onClickBtnGetMore = () => {
    post_event(`Destaques/ClickVerTudo/${contentFilter}`, 'TRUE')
    highlightId && history.push(SHOW_SHELF(highlightId))
  }

  return {
    content,
    isLoading,
    onClickBook,
    onClickBtnGetMore
  }
}

export { useHighlightsBookShelf }
